"use client";

import {
  getProjectDataSourceCacheKey,
  PROJECT_DATA_SOURCE_CACHE_VERSION,
  PROJECT_DATA_SOURCE_PREFIX,
} from "@/lib/aicheck/cache-keys";
import type {
  AggregatedProjectData,
  ProjectDataConversionUsage,
  ProjectDataSourceDescriptor,
  ProjectDataSourceKind,
  ProjectDataTraceIndex,
} from "@/types/aicheck";

export interface ProjectDataSourceCacheCounts {
  itineraryDays: number;
  itineraryItems: number;
  arrangements: number;
}

export interface ProjectDataSourceCacheEntry {
  sourceKey: string;
  conversionId?: string;
  source: ProjectDataSourceDescriptor;
  counts: ProjectDataSourceCacheCounts;
  usage?: ProjectDataConversionUsage;
  warnings?: string[];
  detailsKey?: string;
  hasDetails?: boolean;
  warningDetails?: string[];
  projectData?: AggregatedProjectData;
  conversionReport?: Record<string, unknown>;
  provenance?: ProjectDataTraceIndex;
  cachedAt: string;
}

interface StoredProjectDataSourceCacheEntry {
  v: number;
  data: ProjectDataSourceCacheEntry;
}

function fnv32(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function projectIdFromSourceSeed(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return 1_000_000_000 + ((hash >>> 0) % 900_000_000);
}

function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    url.hash = "";
    url.protocol = url.protocol.toLowerCase();
    url.hostname = url.hostname.toLowerCase();
    return url.href;
  } catch {
    return trimmed;
  }
}

function fileIdentity(file: File): string {
  const maybePath =
    "webkitRelativePath" in file && typeof file.webkitRelativePath === "string"
      ? file.webkitRelativePath
      : "";
  return [maybePath || file.name, file.size].join(":");
}

export function buildProjectDataSourceKey(params: {
  sourceKind: ProjectDataSourceKind;
  file?: File | null;
  url?: string;
  crmProjectId?: string | number | null;
  converter?: string;
  useLLM?: boolean;
}): string | null {
  const sourceKind = params.sourceKind;
  let identity = "";
  if (sourceKind === "crm-api") {
    const projectId = String(params.crmProjectId ?? "").trim();
    if (!projectId) return null;
    identity = `crm:${projectId}`;
  } else if (params.file) {
    identity = `file:${fileIdentity(params.file)}`;
  } else if (params.url?.trim()) {
    identity = `url:${normalizeUrl(params.url)}`;
  } else {
    return null;
  }

  const raw = [
    sourceKind,
    params.converter ?? "",
    params.useLLM === false ? "no-llm" : "llm",
    identity,
  ].join("|");
  return `${sourceKind}:${fnv32(raw)}`;
}

export function projectDataSourceSeed(
  source: ProjectDataSourceDescriptor | null | undefined,
  fallbackSeed = "",
): string {
  if (!source) return fallbackSeed;
  const fileIdentity =
    source.origin?.fileName && typeof source.origin.size === "number"
      ? `file:${source.origin.fileName}:${source.origin.size}`
      : undefined;
  return (
    source.origin?.cacheKey ||
    source.origin?.sha256 ||
    source.origin?.url ||
    fileIdentity ||
    source.dataDigest ||
    source.sourceId ||
    fallbackSeed
  );
}

export function projectIdFromProjectDataSource(
  source: ProjectDataSourceDescriptor | null | undefined,
  fallbackSeed = "",
): number {
  const crmProjectId = source?.origin?.crmProjectId;
  if (
    typeof crmProjectId === "number" &&
    Number.isFinite(crmProjectId) &&
    crmProjectId > 0
  ) {
    return crmProjectId;
  }
  return projectIdFromSourceSeed(projectDataSourceSeed(source, fallbackSeed));
}

export function readProjectDataSourceCache(
  sourceKey: string | null | undefined,
): ProjectDataSourceCacheEntry | null {
  if (!sourceKey) return null;
  try {
    const raw = localStorage.getItem(getProjectDataSourceCacheKey(sourceKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredProjectDataSourceCacheEntry;
    if (
      parsed?.v !== PROJECT_DATA_SOURCE_CACHE_VERSION ||
      !parsed.data?.source
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeProjectDataSourceCache(
  entry: ProjectDataSourceCacheEntry,
): void {
  try {
    localStorage.setItem(
      getProjectDataSourceCacheKey(entry.sourceKey),
      JSON.stringify({
        v: PROJECT_DATA_SOURCE_CACHE_VERSION,
        data: entry,
      } satisfies StoredProjectDataSourceCacheEntry),
    );
  } catch (error) {
    console.warn("[project-data-source-cache] Failed to save cache", error);
  }
}

export function listProjectDataSourceCaches(): ProjectDataSourceCacheEntry[] {
  const entries: ProjectDataSourceCacheEntry[] = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(PROJECT_DATA_SOURCE_PREFIX)) continue;
      const sourceKey = key.slice(PROJECT_DATA_SOURCE_PREFIX.length);
      const entry = readProjectDataSourceCache(sourceKey);
      if (entry) entries.push(entry);
    }
  } catch {
    return entries;
  }
  return entries.sort((a, b) => b.cachedAt.localeCompare(a.cachedAt));
}

export function removeProjectDataSourceCache(sourceKey: string): void {
  try {
    localStorage.removeItem(getProjectDataSourceCacheKey(sourceKey));
  } catch {
    // ignore
  }
}
